import type { ContentCardProps } from '../../types';
import { Card, Icon, Stack } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import { getIconColor } from '~/utils';
import { CardFooter } from '../CardFooter';
import { CardDescription, CardTitle } from '../CardText';
import { buildCardMetadata } from '../../utils';

export interface ScormCardProps extends ContentCardProps {
  onStart?: () => void;
  duration?: number | null;
}

const { DEFAULT_COVER_IMAGES } = window.APP_CONSTANTS;

export const ScormCard = ({
  name,
  imageSrc,
  description,
  assignmentStatus,
  duration,
  onStart,
  tags = [],
  skills = [],
  overflowMenuItems = {},
  hideContentCTA = false,
  actions,
}: ScormCardProps) => {
  const theme = useTheme();

  const START_NOW = I18n.t('js.learner.libraries.start_now');
  const CONTINUE = I18n.t('js.learner.libraries.continue');

  const metadata = buildCardMetadata(duration, skills, tags, theme.colors.on[600]);

  const iconColor = getIconColor(theme.colors.decorative.orange[500]);

  return (
    <Card onPress={onStart}>
      {imageSrc && (
        <Card.Hero
          src={imageSrc}
          errorImage={DEFAULT_COVER_IMAGES.SCORM}
          icon={<Icon color={iconColor} name="scorm:regular" size="lg" />}
        />
      )}
      <Card.Container data-testid="library-card">
        <Card.Content>
          <Card.Body>
            <Stack size={4}>
              {!imageSrc && <Card.Icon iconColor={iconColor} iconName="scorm:regular" />}
              <Stack size={2}>
                <CardTitle text={name} />
                {metadata.length ? <Card.Metadata metadata={metadata} /> : null}
                <CardDescription text={description} color={theme.colors.on[500]} />
              </Stack>
            </Stack>
          </Card.Body>
        </Card.Content>
      </Card.Container>
      <CardFooter
        assignmentStatus={assignmentStatus}
        inProgressMessage={CONTINUE}
        startContentMessage={START_NOW}
        onStart={onStart}
        overflowMenuItems={overflowMenuItems}
        hideContentCTA={hideContentCTA}
        actions={actions}
      />
    </Card>
  );
};
